import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import AdminApplications from '../components/admin/AdminApplications';
import AdminProducts from '../components/admin/AdminProducts';
import AdminOrders from '../components/admin/AdminOrders';
import AdminInquiries from '../components/admin/AdminInquiries';
import AdminQuotes from '../components/admin/AdminQuotes';
import BrandAccessModal from '../components/admin/BrandAccessModal';
import ReplyModal from '../components/admin/ReplyModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand-specific category mapping
const BRAND_CATEGORIES = {
  Haiwell: ['PLC', 'HMI', 'Other'],
  Supmea: ['Flow', 'Level', 'Pressure', 'Analyser', 'Other'],
  Savch: ['VFD', 'Servo', 'Other'],
  'Yotta Control': ['Controllers', 'HMI', 'Smart Distributed I/O', 'IoT Gateway', 'Other'],
};

const DEFAULT_DISCOUNTS = { Haiwell: 15, Supmea: 15, Savch: 15, 'Yotta Control': 15 };

const AdminDashboard = () => {
  const { isAdmin, loading: authLoading } = useAuth();

  // Data
  const [applications, setApplications] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tabs
  const [activeTab, setActiveTab] = useState('applications');

  // Products — upload + filters
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedBrandForUpload, setSelectedBrandForUpload] = useState('Haiwell');
  const [selectedCategoryForUpload, setSelectedCategoryForUpload] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');

  // Inquiries — reply modal
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  // Approval modal
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [approvalBrandDiscounts, setApprovalBrandDiscounts] = useState(DEFAULT_DISCOUNTS);
  const [approvalApprovedBrands, setApprovalApprovedBrands] = useState([]);

  // Edit-partner modal
  const [editPartnerModalOpen, setEditPartnerModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [editBrandDiscounts, setEditBrandDiscounts] = useState(DEFAULT_DISCOUNTS);
  const [editApprovedBrands, setEditApprovedBrands] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      fetchApplications();
      fetchOrders();
      fetchProducts();
      fetchInquiries();
    }
  }, [isAdmin]);

  // ---------- Fetchers ----------
  const fetchApplications = async () => {
    try {
      const { data } = await axios.get(`${API}/partner/applications`, { withCredentials: true });
      setApplications(data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/partner/orders/all`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      toast.error('Failed to load orders');
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products/list`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchInquiries = async () => {
    try {
      const { data } = await axios.get(`${API}/contact/inquiries`, { withCredentials: true });
      setInquiries(data);
    } catch (error) {
      toast.error('Failed to load inquiries');
    }
  };

  // ---------- Products ----------
  const handleBrandChange = (newBrand) => {
    setSelectedBrandForUpload(newBrand);
    setSelectedCategoryForUpload('');
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return toast.error('Please select a file');
    if (!selectedBrandForUpload) return toast.error('Please select a brand');
    if (!selectedCategoryForUpload) return toast.error('Please select a category');

    const matchingProductCount = products.filter(
      (p) => p.brand === selectedBrandForUpload && p.category === selectedCategoryForUpload
    ).length;
    const confirmMessage =
      matchingProductCount > 0
        ? `This will replace ${matchingProductCount} existing ${selectedBrandForUpload} - ${selectedCategoryForUpload} products. Continue?`
        : `This will upload new ${selectedBrandForUpload} - ${selectedCategoryForUpload} products. Continue?`;
    if (!window.confirm(confirmMessage)) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('brand', selectedBrandForUpload);
    formData.append('category', selectedCategoryForUpload);

    try {
      toast.info(
        `Uploading ${selectedBrandForUpload} - ${selectedCategoryForUpload} products...`,
        { duration: 2000 }
      );
      const { data } = await axios.post(`${API}/products/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${data.message} (${selectedBrandForUpload} - ${selectedCategoryForUpload})`);
      setSelectedFile(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload products');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/products/template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // ---------- Applications ----------
  const handleApprove = (applicationId) => {
    const application = applications.find((a) => a.id === applicationId);
    setSelectedApplication(application);
    setApprovalBrandDiscounts(DEFAULT_DISCOUNTS);
    setApprovalApprovedBrands([]);
    setApprovalModalOpen(true);
  };

  const toggleApprovalBrand = (brand) => {
    setApprovalApprovedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const confirmApproval = async () => {
    if (approvalApprovedBrands.length === 0) {
      toast.error('Please select at least one brand to approve');
      return;
    }
    try {
      const { data } = await axios.put(
        `${API}/partner/applications/${selectedApplication.id}/approve`,
        { brand_discounts: approvalBrandDiscounts, approved_brands: approvalApprovedBrands },
        { withCredentials: true }
      );
      toast.success(data.message || 'Application approved! Credentials sent via email.');
      const discountSummary = Object.entries(approvalBrandDiscounts)
        .map(([brand, discount]) => `${brand}: ${discount}%`)
        .join(', ');
      toast.info(`Approved brands: ${approvalApprovedBrands.join(', ')}`, { duration: 5000 });
      toast.info(`Discounts: ${discountSummary}`, { duration: 5000 });
      setApprovalModalOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    }
  };

  const handleEditPartner = async (application) => {
    try {
      const { data: partners } = await axios.get(`${API}/partner/users/partners`, {
        withCredentials: true,
      });
      const partner = partners.find((p) => p.email === application.email);
      if (partner) {
        setSelectedPartner(partner);
        setEditBrandDiscounts(partner.brand_discounts || DEFAULT_DISCOUNTS);
        setEditApprovedBrands(partner.approved_brands || []);
        setEditPartnerModalOpen(true);
      } else {
        toast.error(`Partner user not found. Email: ${application.email}`);
      }
    } catch (error) {
      toast.error(`Failed to load partner data: ${error.response?.data?.detail || error.message}`);
    }
  };

  const toggleEditBrand = (brand) => {
    setEditApprovedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const confirmEditPartner = async () => {
    if (editApprovedBrands.length === 0) {
      toast.error('Please select at least one brand');
      return;
    }
    try {
      await axios.put(
        `${API}/partner/users/${selectedPartner.id}/brand-discounts`,
        { brand_discounts: editBrandDiscounts },
        { withCredentials: true }
      );
      await axios.put(
        `${API}/partner/users/${selectedPartner.id}/approved-brands`,
        { approved_brands: editApprovedBrands },
        { withCredentials: true }
      );
      toast.success('Partner brands and discounts updated successfully!');
      const discountSummary = Object.entries(editBrandDiscounts)
        .map(([brand, discount]) => `${brand}: ${discount}%`)
        .join(', ');
      toast.info(`Approved brands: ${editApprovedBrands.join(', ')}`, { duration: 5000 });
      toast.info(`Discounts: ${discountSummary}`, { duration: 5000 });
      setEditPartnerModalOpen(false);
      fetchApplications();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update partner';
      toast.error(`Update failed: ${errorMsg}`);
    }
  };

  const handleReject = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;
    try {
      await axios.put(
        `${API}/partner/applications/${applicationId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success('Application rejected');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to reject application');
    }
  };

  const handleResendCredentials = async (applicationId) => {
    if (
      !window.confirm(
        'Generate a NEW password and send to partner via email?\n\nNote: This will reset their current password.'
      )
    )
      return;
    try {
      const { data } = await axios.post(
        `${API}/partner/applications/${applicationId}/resend-credentials`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message || 'New password sent to partner');
      if (data.note) toast.info(data.note);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credentials');
    }
  };

  const handleForwardApplication = async (applicationId) => {
    if (!window.confirm('Forward this application to info@ewss.co.za?')) return;
    try {
      await axios.post(
        `${API}/partner/applications/${applicationId}/forward`,
        {},
        { withCredentials: true }
      );
      toast.success('Application forwarded to info@ewss.co.za!');
    } catch (error) {
      toast.error('Failed to forward application');
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (
      !window.confirm('Are you sure you want to delete this application? This action cannot be undone.')
    )
      return;
    try {
      await axios.delete(`${API}/partner/applications/${applicationId}`, { withCredentials: true });
      toast.success('Application deleted successfully');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  // ---------- Orders ----------
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/partner/orders/${orderId}/status?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handleForwardOrder = async (orderId) => {
    if (!window.confirm('Forward this order to info@ewss.co.za?')) return;
    try {
      await axios.post(`${API}/partner/orders/${orderId}/forward`, {}, { withCredentials: true });
      toast.success('Order forwarded to info@ewss.co.za!');
    } catch (error) {
      toast.error('Failed to forward order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.'))
      return;
    try {
      await axios.delete(`${API}/partner/orders/${orderId}`, { withCredentials: true });
      toast.success('Order deleted successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  // ---------- Inquiries ----------
  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await axios.put(
        `${API}/contact/inquiries/${inquiryId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Inquiry status updated!');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update inquiry status');
    }
  };

  const handleReplySubmit = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/contact/inquiries/${selectedInquiry.id}/reply`,
        { reply_message: replyMessage },
        { withCredentials: true }
      );
      toast.success('Reply saved successfully!');
      toast.info(`Customer email: ${data.customer_email}`);
      setReplyMessage('');
      setSelectedInquiry(null);
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to save reply');
    }
  };

  const handleForwardInquiry = async (inquiryId) => {
    if (!window.confirm('Forward this inquiry to info@ewss.co.za?')) return;
    try {
      await axios.post(`${API}/contact/inquiries/${inquiryId}/forward`, {}, {
        withCredentials: true,
      });
      toast.success('Inquiry forwarded to info@ewss.co.za!');
    } catch (error) {
      toast.error('Failed to forward inquiry');
    }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    if (
      !window.confirm('Are you sure you want to delete this inquiry? This action cannot be undone.')
    )
      return;
    try {
      await axios.delete(`${API}/contact/inquiries/${inquiryId}`, { withCredentials: true });
      toast.success('Inquiry deleted successfully');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  // ---------- Render ----------
  // While AuthContext is still checking (page refresh / direct hit), keep
  // showing a loader — otherwise `isAdmin` is false during that window and
  // Navigate would kick the user off before /auth/me resolves.
  if (authLoading) {
    return (
      <div className="min-h-screen pt-20 bg-ewss-navy flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-ewss-navy flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'applications',
      label: `Applications (${applications.filter((a) => a.status === 'pending').length})`,
    },
    { id: 'products', label: `Products (${products.length})` },
    { id: 'quotes', label: `Project Quotes` },
    { id: 'orders', label: `Orders (${orders.length})` },
    { id: 'inquiries', label: `Contact Enquiries (${inquiries.length})` },
  ];

  return (
    <div className="min-h-screen pt-20 bg-ewss-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-400">Manage partner applications, products, and orders</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-700" data-testid="admin-dashboard-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === t.id
                  ? 'text-ewss-cyan border-b-2 border-ewss-cyan'
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'applications' && (
          <AdminApplications
            applications={applications}
            onApprove={handleApprove}
            onReject={handleReject}
            onEdit={handleEditPartner}
            onResend={handleResendCredentials}
            onForward={handleForwardApplication}
            onDelete={handleDeleteApplication}
          />
        )}

        {activeTab === 'products' && (
          <AdminProducts
            products={products}
            brandCategories={BRAND_CATEGORIES}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            selectedBrandForUpload={selectedBrandForUpload}
            onBrandChange={handleBrandChange}
            selectedCategoryForUpload={selectedCategoryForUpload}
            setSelectedCategoryForUpload={setSelectedCategoryForUpload}
            handleFileUpload={handleFileUpload}
            downloadTemplate={downloadTemplate}
            productSearch={productSearch}
            setProductSearch={setProductSearch}
            filterBrand={filterBrand}
            setFilterBrand={setFilterBrand}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
          />
        )}

        {activeTab === 'orders' && (
          <AdminOrders
            orders={orders}
            onUpdateStatus={updateOrderStatus}
            onForward={handleForwardOrder}
            onDelete={handleDeleteOrder}
          />
        )}

        {activeTab === 'quotes' && (
          <AdminQuotes products={products} />
        )}

        {activeTab === 'inquiries' && (
          <AdminInquiries
            inquiries={inquiries}
            onUpdateStatus={updateInquiryStatus}
            onReply={setSelectedInquiry}
            onForward={handleForwardInquiry}
            onDelete={handleDeleteInquiry}
          />
        )}

        {/* Modals */}
        <ReplyModal
          inquiry={selectedInquiry}
          replyMessage={replyMessage}
          setReplyMessage={setReplyMessage}
          onSubmit={handleReplySubmit}
          onCancel={() => {
            setSelectedInquiry(null);
            setReplyMessage('');
          }}
        />

        <BrandAccessModal
          open={approvalModalOpen && !!selectedApplication}
          title="Approve Partner Application"
          subject={
            selectedApplication
              ? {
                  label1: 'Company',
                  value1: selectedApplication.company_name,
                  label2: 'Contact',
                  value2: `${selectedApplication.contact_person} (${selectedApplication.email})`,
                }
              : null
          }
          approvedBrands={approvalApprovedBrands}
          toggleBrand={toggleApprovalBrand}
          brandDiscounts={approvalBrandDiscounts}
          setBrandDiscounts={setApprovalBrandDiscounts}
          onConfirm={confirmApproval}
          onCancel={() => setApprovalModalOpen(false)}
          confirmLabel="Approve & Send Credentials"
          infoText="Default: 15% for all brands. Adjust as needed based on partner volume."
        />

        <BrandAccessModal
          open={editPartnerModalOpen && !!selectedPartner}
          title="Edit Partner Access & Discounts"
          subject={
            selectedPartner
              ? {
                  label1: 'Partner',
                  value1: selectedPartner.company,
                  label2: 'Contact',
                  value2: `${selectedPartner.name} (${selectedPartner.email})`,
                }
              : null
          }
          approvedBrands={editApprovedBrands}
          toggleBrand={toggleEditBrand}
          brandDiscounts={editBrandDiscounts}
          setBrandDiscounts={setEditBrandDiscounts}
          onConfirm={confirmEditPartner}
          onCancel={() => setEditPartnerModalOpen(false)}
          confirmLabel="Save Changes"
          infoText="Adjust discounts based on partner volume and relationship."
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
